import { useRef, useEffect } from 'react'
import type {
  AlbumConfig,
  InvestmentDestination,
  StickerInvestment,
  ThemeDefinition
} from '../types'
import { formatCurrency, stickerFact, stickerSourceUrl, stickerTitle, stickerValue } from '../data'
import { useI18n } from '../i18n/context'

interface Props {
  selectedSticker: number
  stickers: Record<number, StickerInvestment>
  album: AlbumConfig
  theme: ThemeDefinition
  selectedDestinationId: string
  comments: string
  onDestinationChange: (id: string) => void
  onCommentsChange: (v: string) => void
  onSave: () => void
  onReset: () => void
  onClose: () => void
  onGoToConfig: () => void
  onOpenExternalLink: (url: string) => void
}

const MAX_COMMENT_LENGTH = 500

export function StickerModal({
  selectedSticker,
  stickers,
  album,
  theme,
  selectedDestinationId,
  comments,
  onDestinationChange,
  onCommentsChange,
  onSave,
  onReset,
  onClose,
  onGoToConfig,
  onOpenExternalLink
}: Props): JSX.Element {
  const { t } = useI18n()
  const existing = stickers[selectedSticker]
  const sourceUrl = stickerSourceUrl(theme, selectedSticker)
  const stickerDetails = stickerFact(theme, selectedSticker)
  const dialogRef = useRef<HTMLElement>(null)

  useEffect(() => {
    const el = dialogRef.current
    if (!el) return
    const focusable = el.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
    if (focusable.length > 0) focusable[0].focus()

    function handleKeyDown(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        onClose()
        return
      }
      if (e.key !== 'Tab') return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault()
          last.focus()
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <section
        ref={dialogRef}
        className="modal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="panel-heading">
          <div>
            <p className="eyebrow">{t.stickerInvestment}</p>
            <h2 id="modal-title">
              #{selectedSticker} ·{' '}
              {formatCurrency(stickerValue(selectedSticker, album.size), album.currency)}
            </h2>
          </div>
          {existing && (
            <button
              className="ghost-button"
              onClick={onReset}
              type="button"
              aria-label={`${t.resetSticker} ${selectedSticker}`}
            >
              {t.resetSticker}
            </button>
          )}
        </div>

        <p className="modal-copy">{stickerTitle(theme, selectedSticker)}</p>
        {stickerDetails && <p className="modal-copy">{stickerDetails}</p>}
        {existing && sourceUrl && (
          <button
            className="ghost-button"
            type="button"
            onClick={() => onOpenExternalLink(sourceUrl)}
            style={{ marginBottom: '12px' }}
          >
            Open external source
          </button>
        )}

        {album.savedInvestments.length === 0 ? (
          <div className="empty-state">
            <strong>{t.noDestinationsYet}</strong>
            <p>{t.noDestinationsDesc}</p>
            <button
              className="primary-button"
              onClick={() => {
                onClose()
                onGoToConfig()
              }}
              type="button"
              style={{ marginTop: '12px' }}
            >
              {t.goToConfiguration}
            </button>
          </div>
        ) : (
          <>
            <label className="field">
              <span>{t.investIn}</span>
              <select
                value={selectedDestinationId}
                onChange={(e) => onDestinationChange(e.target.value)}
                aria-label={t.investIn}
              >
                {album.savedInvestments.map((dest: InvestmentDestination) => (
                  <option key={dest.id} value={dest.id}>
                    {dest.name} ({dest.kind})
                  </option>
                ))}
              </select>
            </label>

            <label className="field">
              <span>{t.comments}</span>
              <textarea
                rows={4}
                value={comments}
                maxLength={MAX_COMMENT_LENGTH}
                onChange={(e) => onCommentsChange(e.target.value)}
                placeholder={t.commentsPlaceholder}
                aria-label={t.comments}
              />
            </label>

            <div className="modal-actions">
              <button className="ghost-button" onClick={onClose} type="button">
                {t.cancel}
              </button>
              <button className="primary-button" onClick={onSave} type="button">
                {existing ? t.updateInvestment : t.invest}
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  )
}
